import { motion } from 'framer-motion';
import { Server, Cpu, Network, Shield, Bell, Lock, Activity } from 'lucide-react';

const ArchitecturePage = () => {
  const techStack = [
    { category: 'Frontend', items: ['React 18', 'TypeScript', 'Vite', 'Framer Motion', 'TailwindCSS'] },
    { category: 'Backend', items: ['FastAPI', 'Python 3.11', 'PostgreSQL', 'Redis', 'WebSockets'] },
    { category: 'Agents', items: ['Python systemd services', 'Autonomous polling', 'Task queues', 'Event-driven'] },
    { category: 'AI/ML', items: ['Ollama', 'Local LLMs', 'GPU acceleration', 'RAG pipelines'] },
    { category: 'Infrastructure', items: ['Docker', 'Nginx', 'Let\'s Encrypt', 'Ubuntu/Proxmox'] }
  ];

  const agents = [
    {
      name: 'Guardian',
      icon: Shield,
      color: 'text-green-500',
      role: 'Security Monitoring',
      tech: 'Pi-hole DNS log analysis, threat intelligence, domain blocking',
      flow: 'Polls DNS logs → Analyzes patterns → Blocks threats → Reports findings'
    },
    {
      name: 'Caretaker',
      icon: Activity,
      color: 'text-orange-500',
      role: 'System Health',
      tech: 'psutil, systemd monitoring, disk analysis, service health checks',
      flow: 'Checks resources → Identifies issues → Creates recommendations → Executes cleanup'
    },
    {
      name: 'Network Agent',
      icon: Network,
      color: 'text-blue-500',
      role: 'Device Discovery',
      tech: 'fping, nmap, hostname resolution, PostgreSQL tracking',
      flow: 'Ping sweep → NMAP scan → Resolve names → Update database → Snapshot state'
    },
    {
      name: 'Herald',
      icon: Bell,
      color: 'text-purple-500',
      role: 'Communication',
      tech: 'Discord webhooks, SMTP, Twilio SMS, multi-channel routing',
      flow: 'Receives events → Routes by severity → Formats messages → Delivers notifications'
    }
  ];

  const principles = [
    {
      title: 'Local-First',
      description: 'Zero cloud dependencies. All processing happens on your hardware.',
      icon: Server
    },
    {
      title: 'Privacy by Design',
      description: 'Data never leaves your network. Complete control over information.',
      icon: Lock
    },
    {
      title: 'Autonomous but Supervised',
      description: 'Agents operate independently but require human approval for critical actions.',
      icon: Cpu
    },
    {
      title: 'Production-Grade',
      description: 'Systemd services, health checks, error handling, logging, and monitoring.',
      icon: Shield
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-bold text-green-400 mb-4">
            Architecture Deep Dive
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Multi-agent ecosystem built for autonomy, reliability, and local-first AI
          </p>
        </motion.div>

        {/* System Diagram */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-16 bg-black/50 border border-green-500/20 rounded-lg p-8"
        >
          <h2 className="text-3xl font-bold text-green-400 mb-6 text-center">System Flow</h2>
          <div className="text-gray-300 font-mono text-sm space-y-2">
            <div>User/Browser → Nginx (HTTPS + Auth) → Frontend (React SPA)</div>
            <div>Frontend → WebSocket → Backend API (FastAPI)</div>
            <div>Backend ← PostgreSQL (devices, tasks, logs)</div>
            <div>Backend ← Redis (metrics, cache)</div>
            <div>Backend ← 4 Agent Services (systemd)</div>
            <div>Agents → External Systems (Pi-hole, network, Discord)</div>
            <div>Agents → Ollama (local LLM, GPU-accelerated)</div>
          </div>
        </motion.div>

        {/* Tech Stack */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-green-400 mb-6">Technology Stack</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {techStack.map((stack) => (
              <div
                key={stack.category}
                className="bg-black/50 border border-green-500/20 rounded-lg p-6"
              >
                <h3 className="text-xl font-bold text-green-400 mb-3">{stack.category}</h3>
                <ul className="space-y-2">
                  {stack.items.map((item) => (
                    <li key={item} className="text-gray-300">
                      <span className="text-green-400 mr-2">▸</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Agents */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-green-400 mb-6">Agent Architecture</h2>
          <div className="space-y-6">
            {agents.map((agent, index) => (
              <motion.div
                key={agent.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="bg-black/50 border border-green-500/20 rounded-lg p-6"
              >
                <div className="flex items-start space-x-4">
                  <agent.icon className={`w-12 h-12 ${agent.color}`} />
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-green-400 mb-1">{agent.name}</h3>
                    <p className="text-lg text-gray-400 mb-3">{agent.role}</p>
                    <p className="text-gray-300 mb-2">
                      <span className="text-green-400 font-semibold">Tech:</span> {agent.tech}
                    </p>
                    <p className="text-gray-300 font-mono text-sm">
                      <span className="text-green-400 font-semibold">Flow:</span> {agent.flow}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Design Principles */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <h2 className="text-3xl font-bold text-green-400 mb-6">Design Principles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {principles.map((principle) => (
              <div
                key={principle.title}
                className="bg-black/50 border border-green-500/20 rounded-lg p-6"
              >
                <div className="flex items-start space-x-4">
                  <principle.icon className="w-10 h-10 text-green-400 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-green-400 mb-2">{principle.title}</h3>
                    <p className="text-gray-300">{principle.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ArchitecturePage;
